import React, { useState } from 'react';
import { BPForm } from '@common/BPForm';
import { AuthField } from '../Common/AuthField';
import { Layout } from './Layout';
import { Button, Divider, Header, Message, Segment, Grid } from 'semantic-ui-react';
import * as yup from 'yup';
import { userManageClient } from '../userManageClient';
import { DeletePersonalDataRequest } from '@app/api2/api';
import { buildUrl, AppRoutes } from '../../AppRoutes';
import { BPButton } from '@/components/bp/bpbutton';

interface DeleteAccountFormValues {
    password: string;
}

export const PersonalData: React.FC = () => {
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [deleteError, setDeleteError] = useState<string>('');
    const [downloadError, setDownloadError] = useState<string>('');

    const initialValues: DeleteAccountFormValues = {
        password: ''
    };

    const validationSchema: yup.SchemaOf<DeleteAccountFormValues> = yup.object().shape({
        password: yup.string()
            .required('Password is required to delete your account')
    });

    const handleDownload = async () => {
        setDownloadError('');
        setDownloadLoading(true);
        
        try {
            const result = await userManageClient.downloadPersonalData();

            if (result.success && result.personalData) {
                // Create and download JSON file
                const dataStr = JSON.stringify(result.personalData, null, 2);
                const dataBlob = new Blob([dataStr], { type: 'application/json' });
                const url = URL.createObjectURL(dataBlob);
                
                const link = document.createElement('a');
                link.href = url;
                link.download = 'PersonalData.json';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                URL.revokeObjectURL(url);
            } else {
                setDownloadError('Failed to download personal data.');
            }
        } catch (error) {
            console.error('Download personal data error:', error);
            setDownloadError('An error occurred while downloading your personal data. Please try again.');
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleDeleteAccount = async (values: DeleteAccountFormValues) => {
        if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
            return;
        }

        setDeleteError('');
        
        try {
            const request: DeletePersonalDataRequest = {
                password: values.password
            };

            const result = await userManageClient.deletePersonalData(request);

            if (result.success) {
                // Account deleted successfully, redirect to login
                window.location.assign(buildUrl(AppRoutes.Login, {}));
            } else {
                setDeleteError(result.errors?.join(', ') || 'Failed to delete account.');
            }
        } catch (error) {
            console.error('Delete account error:', error);
            setDeleteError('An error occurred while deleting your account. Please try again.');
        }
    };

    return (
        <Layout>
            <Header as="h1">Personal Data</Header>
            
            <Grid stackable>
                <Grid.Column width={8}>
                    <Segment>
                        <Header as="h3">Download your data</Header>
                        <p>
                            Click here to download an archive of your personal data that we store about you.
                        </p>

                        {downloadError && (
                            <Message error>
                                {downloadError}
                            </Message>
                        )}

                        <BPButton 
                            onClick={handleDownload}
                            primary
                            disabled={downloadLoading}
                            loading={downloadLoading}
                        >
                            Download
                        </BPButton>
                    </Segment>
                </Grid.Column>
                
                <Grid.Column width={8}>
                    <Segment>
                        <Header as="h3" color="red">Delete Account</Header>
                        <Message warning>
                            <Message.Header>Warning</Message.Header>
                            <p>
                                <strong>Deleting this data will permanently remove your account, and this cannot be recovered.</strong>
                            </p>
                        </Message>

                        {deleteError && (
                            <Message error>
                                {deleteError}
                            </Message>
                        )}

                        <BPForm
                            initialValues={initialValues}
                            vaildationSchema={validationSchema}
                            onSubmit={handleDeleteAccount}
                            hideButtons={true}
                        >
                            {(formProps) => (
                                <>
                                    <AuthField
                                        name="password"
                                        label="Password"
                                        type="password"
                                        autoComplete="current-password"
                                        placeholder="Enter your password to confirm deletion"
                                    />
                                    
                                    <Divider />
                                    
                                    <BPButton 
                                        type="submit" 
                                        negative 
                                        size="large" 
                                        disabled={formProps.isSubmitting}
                                        loading={formProps.isSubmitting}
                                    >
                                        Delete data and close my account
                                    </BPButton>
                                </>
                            )}
                        </BPForm>
                    </Segment>
                </Grid.Column>
            </Grid>
        </Layout>
    );
};